import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { profilesService, Profile } from '../../services/supabase/profiles';
import { CLOCK_THEMES } from '../../styles/clockThemes';

const VALID_THEMES = new Set(Object.keys(CLOCK_THEMES));
const FALLBACK_THEME = 'playful_focus';

function normalizeTheme(theme: string | undefined): string | undefined {
  if (theme === undefined) return undefined;
  if (VALID_THEMES.has(theme)) return theme;
  // Map known orphan values lingering in production
  if (theme === 'default') return FALLBACK_THEME;
  if (theme === 'kids') return 'kids_light';
  return FALLBACK_THEME;
}

export function useProfiles() {
  const queryClient = useQueryClient();

  const { data: profiles = [], isLoading, error, refetch } = useQuery({
    queryKey: ['profiles'],
    queryFn: profilesService.getProfiles
  });

  const createMutation = useMutation({
    mutationFn: ({ displayName, theme }: { displayName: string; theme?: string }) =>
      profilesService.createProfile(displayName, normalizeTheme(theme)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Profile> }) => {
      const safeUpdates = updates.theme !== undefined
        ? { ...updates, theme: normalizeTheme(updates.theme) }
        : updates;
      return profilesService.updateProfile(id, safeUpdates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: profilesService.deleteProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
    }
  });

  return {
    profiles,
    loading: isLoading,
    error,
    refetch,
    createProfile: createMutation.mutateAsync,
    updateProfile: updateMutation.mutateAsync,
    deleteProfile: deleteMutation.mutateAsync
  };
}

